import type { ComponentType, FC } from 'react'

import { ExpandableDescription, ExternalLink, Icon, StyledText, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import ContentCopyIcon from 'mdi-react/ContentCopyIcon'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { usePolicyEditAccess, useUpdateProduct } from '@/modules/ecom-policy/policy/hooks'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'

import type { ProductMoreInfoProps } from '.'

export const ProductInfoTab: FC<
  ProductMoreInfoProps & {
    isGroupedProduct?: boolean
    onClose: () => void
  }
> = ({ isSelected, policyId, productId, isGroupedProduct = false, onClose }) => {
  const { data: policy } = useQuery(queries.policyDetails(policyId), () =>
    ecomPolicyResources.getPolicyDetails(policyId)
  )
  const { data } = useQuery(
    queries.policyProducts(policyId),
    () => {
      if (!policy) throw Error('Policy data is missing!')
      return ecomPolicyResources.getPolicyProducts(policyId, policy.vendorId)
    },
    { enabled: !!policy }
  )
  const product = data?.products.find(p => p.id === productId)
  if (!product) return null
  const {
    isMandatory,
    quantityLimit,
    desc: description,
    alias,
    aliasDescription,
    name,
    sku,
    code,
    manufacturerProductNo,
    manufacturerName,
    webUrl,
    priceId,
  } = product

  const translate = useTranslation()
  const hasEditAccess = usePolicyEditAccess()
  const { updateProduct, isUpdating } = useUpdateProduct()

  const handleProductSelect = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!hasEditAccess || isSelected) {
      return
    }

    updateProduct(
      {
        policyId,
        payload: {
          products: [
            {
              productId,
              isSelected: !isSelected,
              priceId: priceId || null,
              isMandatory: Boolean(isMandatory),
              quantityLimit: quantityLimit || 1,
              alias,
              aliasDescription,
            },
          ],
        },
      },
      {
        onSuccess: onClose,
      }
    )
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <DataPoints className="!m-0 !p-0">
        {/* Article name */}
        <DataPoint
          className="!mx-0 !flex-[100%]"
          header={<Translation id="Product name" />}
          value={
            <StyledText size="large" colorMode="black" fontWeight="bold">
              {name}
            </StyledText>
          }
        />

        {/* Product vendor unique ID / Article number */}
        <DataPoint
          className="!mx-0 !flex-[100%]"
          header={sku ? <Translation id="Vendor unique ID" /> : <Translation id="Article No." />}
          value={
            <span className="flex items-center">
              {sku || code}
              <Button
                size="2xs"
                variant="tertiary"
                Icon={<ContentCopyIcon />}
                className="m-0 mb-1 ml-1 p-0"
                data-tooltip-id="copy"
                data-tooltip-content={translate('Copy')}
                data-event="click focus"
                onClick={e => {
                  e.stopPropagation()
                  const copyText = sku || code
                  window.navigator.clipboard.writeText(copyText)
                }}
              >
                <Tooltip id="copy" size="base" />
              </Button>
            </span>
          }
        />

        {/* Product manufacturer name */}
        <DataPoint
          className="!mx-0 !flex-[100%]"
          header={<Translation id="Manufacturer" />}
          value={manufacturerName ? manufacturerName : '-'}
        />

        {/* Product manufacturer number */}
        <DataPoint
          className="!mx-0 !flex-[100%]"
          header={<Translation id="Manufacturer number" />}
          value={
            <span className="flex items-center">
              {manufacturerProductNo ? manufacturerProductNo : '-'}
              <Button
                size="2xs"
                variant="tertiary"
                Icon={<ContentCopyIcon />}
                className="m-0 mb-1 ml-1 p-0"
                data-tooltip-id="copy"
                data-tooltip-content={translate('Copy')}
                data-event="click focus"
                onClick={e => {
                  if (!manufacturerProductNo) {
                    return
                  }
                  e.stopPropagation()
                  window.navigator.clipboard.writeText(manufacturerProductNo)
                }}
              >
                <Tooltip id="copy" size="base" />
              </Button>
            </span>
          }
        />

        {/* Product URL */}
        <DataPoint
          className="!mx-0 !flex-[100%]"
          header={<Translation id="Product page" />}
          value={
            webUrl ? (
              <ExternalLink
                className="hover:cursor-pointer hover:underline"
                hasUnderline={false}
                href={webUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {webUrl}
                <Icon className="ml-1" size="small">
                  <OpenInNewIcon />
                </Icon>
              </ExternalLink>
            ) : (
              '-'
            )
          }
        />

        {/* Product description */}
        <DataPoint
          className="!mx-0 !flex-[100%]"
          header={<Translation id="Description" />}
          value={
            description ? (
              <ExpandableDescription
                description={description || ''}
                showFullText={false}
                TranslationComponent={Translation as ComponentType}
              />
            ) : (
              '-'
            )
          }
        />
      </DataPoints>

      {Boolean(!isSelected || onClose) && (
        <div
          className={twJoin(
            'mt-4 flex flex-col items-center sm:flex-row',
            isSelected ? 'justify-end' : 'justify-between'
          )}
        >
          {Boolean(!isSelected) && (
            <Button
              variant="primary"
              disabled={!hasEditAccess || isUpdating}
              onClick={e => handleProductSelect(e)}
              className="min-w-full sm:min-w-32"
            >
              <Translation id="Select" />
            </Button>
          )}

          {!isGroupedProduct && (
            <Button onClick={onClose} className="min-w-full sm:min-w-32">
              <Translation id="Close" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
